import React, { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';

const blobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export const UPLOAD_TIMEOUT_MS = 45000;

const timedOutUploadError = () => {
  const error = new Error('Upload timed out before the server confirmed the recording.');
  error.code = 'UPLOAD_TIMEOUT';
  error.writeOutcomeUnknown = true;
  return error;
};

export const uploadRecording = async ({
  blob,
  translationId,
  functionUrl = process.env.REACT_APP_UPLOAD_ENDPOINT || '/.netlify/functions/uploadToS3',
  readAsDataUrl = blobAsDataUrl,
  timeoutMs = UPLOAD_TIMEOUT_MS,
}) => {
  const base64Data = await readAsDataUrl(blob);
  const filename = `recordings/${Date.now()}-${Math.random().toString(36).substring(2, 15)}.webm`;
  const isCloudflareUpload = functionUrl.startsWith('/api/');

  console.log('Uploading to:', functionUrl);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(functionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        audioData: base64Data,
        filename,
        contentType: 'audio/webm',
        translationId,
      }),
      signal: controller.signal,
      ...(isCloudflareUpload ? { credentials: 'same-origin', redirect: 'manual' } : {}),
    });
  } catch (error) {
    if (error?.name === 'AbortError' || controller.signal.aborted) {
      throw timedOutUploadError();
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }

  if (response.type === 'opaqueredirect') {
    const error = new Error('Unable to upload recording.');
    error.code = 'AIRTABLE_UNAVAILABLE';
    throw error;
  }

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      // Non-JSON upstream errors are classified below.
    }
    if (errorData && typeof errorData.error === 'string') {
      const error = new Error(errorData.error);
      if (typeof errorData.code === 'string') error.code = errorData.code;
      if (errorData.writeOutcomeUnknown === true) error.writeOutcomeUnknown = true;
      throw error;
    }
    if (isCloudflareUpload && (response.status === 401 || response.status === 403)) {
      const sessionError = new Error('Session is required.');
      sessionError.code = 'SESSION_REQUIRED';
      throw sessionError;
    }
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  const url = data.url || data.location || data.Location;
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error('Upload response did not include a recording URL');
  }
  return {
    attachmentStored: data.attached === true,
    url,
  };
};

const CustomAudioRecorder = ({ onSubmit, onError, selectedMic, onMicChange, translationId }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [progress, setProgress] = useState('00:00');
  const [availableDevices, setAvailableDevices] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [uploading, setUploading] = useState(false);
  const wavesurferRef = useRef(null);
  const recordRef = useRef(null);
  const audioBlobRef = useRef(null);
  const uploadingRef = useRef(false);

  // AWS configuration moved to serverless function

  const createWaveSurfer = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }
    wavesurferRef.current = WaveSurfer.create({
      container: '#waveform',
      waveColor: 'violet',
      interact: false,
      cursorWidth: 0,
    });

    recordRef.current = wavesurferRef.current.registerPlugin(RecordPlugin.create({
      mimeType: 'audio/webm', // Use audio/webm for compatibility
      audioBitsPerSecond: 128000,
      sampleRate: 44100,
      scrollingWaveform: true, // Always enable scrolling waveform
      renderRecordedAudio: false,
    }));

    recordRef.current.on('record-end', (blob) => {
      audioBlobRef.current = blob;
      const audioUrl = URL.createObjectURL(blob);
      setMediaBlobUrl(audioUrl);
      setIsRecording(false);
    });

    recordRef.current.on('record-progress', (time) => {
      const formattedTime = [
        Math.floor((time % 3600000) / 60000), // minutes
        Math.floor((time % 60000) / 1000), // seconds
      ].map((v) => (v < 10 ? '0' + v : v)).join(':');
      setProgress(formattedTime);
    });
  }, []);

  useEffect(() => {
    createWaveSurfer();

    RecordPlugin.getAvailableAudioDevices().then((devices) => {
      setAvailableDevices(devices);
      if (devices.length > 0 && !selectedMic) {
        onMicChange(devices[0].deviceId); // Set default mic
      }
    });

    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
    };
  }, [createWaveSurfer, onMicChange, selectedMic]);

  const startRecording = () => {
    if (recordRef.current) {
      recordRef.current.startRecording({ deviceId: selectedMic }).then(() => {
        setIsRecording(true);
      });
    } else {
      console.error('Record plugin not initialized');
    }
  };

  const stopRecording = () => {
    if (recordRef.current) {
      recordRef.current.stopRecording();
    } else {
      console.error('Record plugin not initialized');
    }
  };

  const handleReRecord = () => {
    setMediaBlobUrl(null);
    setCountdown(3);
    setTimeout(() => setCountdown(2), 1000);
    setTimeout(() => setCountdown(1), 2000);
    setTimeout(() => {
      setCountdown(null);
      startRecording();
    }, 3000);
  };

  const handleSubmit = async () => {
    if (!audioBlobRef.current || uploadingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    try {
      const upload = await uploadRecording({ blob: audioBlobRef.current, translationId });
      await onSubmit(upload.url, { attachmentStored: upload.attachmentStored });
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      if (onError && await onError(error)) return;
      // Display error to user
      alert(`Upload failed: ${error.message}. Please try again or contact support.`);
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  return (
    <div>
      {!mediaBlobUrl ? (
        <div>
          <h1>Press Record to start recording 🎙️</h1>
          <select id="mic-select" value={selectedMic} onChange={(e) => onMicChange(e.target.value)}>
            <option value="" hidden>Select mic</option>
            {availableDevices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || device.deviceId}
              </option>
            ))}
          </select>
          <button id="record" onClick={() => { setCountdown(3); setTimeout(() => setCountdown(2), 1000); setTimeout(() => setCountdown(1), 2000); setTimeout(() => { setCountdown(null); startRecording(); }, 3000); }} disabled={isRecording}>
            Record
          </button>
          {countdown && <div className="modal-overlay"><h2>{countdown}</h2></div>}
          <div id="waveform" style={{ width: '100%', height: '200px', marginBottom: '20px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '1rem' }}></div>
          <p id="progress">{progress}</p>
          {isRecording && (
            <button onClick={stopRecording} className="audio-recorder-button" style={{ marginTop: '20px' }}>
              Finish Recording
            </button>
          )}
        </div>
      ) : (
        <div>
          <h3>Review the Recording:</h3>
          <audio src={mediaBlobUrl} controls style={{ marginBottom: '20px' }} /><br></br>
          <button disabled={uploading} onClick={handleReRecord} className="audio-recorder-button record-again" style={{ backgroundColor: 'grey' }}>
            Record Again
          </button>
          <button disabled={uploading} onClick={handleSubmit} className="audio-recorder-button submit-and-continue" style={{ backgroundColor: 'green', marginLeft: '10px' }}>
            {uploading ? 'Uploading...' : 'Submit and Continue'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomAudioRecorder;
